import React from 'react';
import {
  ChatBubbleOvalLeftIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';

const PostFeed = ({ posts = [], onDeletePost, showDeleteButton = false }) => {
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const postTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - postTime) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return postTime.toLocaleDateString();
  };

  if (posts.length === 0) {
    return (
      <div className="text-center py-12">
        <ChatBubbleOvalLeftIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No posts found</h3>
        <p className="mt-1 text-sm text-gray-500">
          No posts have been shared yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {posts.map((post) => (
        <div key={post._id} className="post-card">
          {/* Post Header */}
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="user-avatar">
                    <span className="text-sm font-semibold">
                      {post.author?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                </div>
                <div className="ml-3">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium text-gray-900">
                      {post.author?.name || 'Unknown User'}
                    </p>
                    <span className={`badge ${
                      post.author?.role === 'Student' ? 'badge-blue' : 'badge-green'
                    }`}>
                      {post.author?.role}
                    </span>
                    {post.author?.branch && (
                      <span className="badge badge-gray">
                        {post.author.branch}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <span>{formatTimeAgo(post.createdAt || post.timestamp)}</span>
                    {(post.club || post.clubId) && (
                      <span>• in {post.club?.name || post.clubId?.name}</span>
                    )}
                  </div>
                </div>
              </div>
              {showDeleteButton && onDeletePost && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onDeletePost(post)}
                    className="text-red-400 hover:text-red-600 p-1"
                    title="Delete post"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Post Content */}
          <div className="p-6">
            <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">{post.content}</p>
            {post.image && (
              <div className="mt-4">
                <img
                  src={post.image}
                  alt="Post content"
                  className="rounded-xl max-w-full h-auto shadow-lg"
                  style={{ maxHeight: '400px' }}
                />
              </div>
            )}
          </div>

          {/* Post Actions & Stats */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 rounded-b-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-full shadow-sm">
                  <HeartSolidIcon className="h-4 w-4 text-red-500" />
                  <span className="text-sm font-medium text-gray-700">
                    {post.likesCount || (Array.isArray(post.likes) ? post.likes.length : 0)}
                  </span>
                </div>
                <div className="flex items-center space-x-2 bg-white px-3 py-1 rounded-full shadow-sm">
                  <ChatBubbleOvalLeftIcon className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-medium text-gray-700">
                    {post.commentsCount || (Array.isArray(post.comments) ? post.comments.length : 0)}
                  </span>
                </div>
              </div>
              <div className="text-xs text-gray-400 font-mono">
                #{post._id.slice(-6)}
              </div>
            </div>

            {/* Comments Preview */}
            {Array.isArray(post.comments) && post.comments.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {post.comments.slice(0, 3).map((comment, index) => (
                    <div key={comment._id || comment.id || index} className="text-sm">
                      <span className="font-medium text-gray-900">
                        {typeof comment.author === 'object' ? comment.author?.name : (comment.authorName || 'Anonymous')}:
                      </span>
                      <span className="text-gray-700 ml-1">{comment.content || comment.text}</span>
                      <span className="text-gray-400 ml-2 text-xs">
                        {formatTimeAgo(comment.createdAt || comment.timestamp)}
                      </span>
                    </div>
                  ))}
                  {post.comments.length > 3 && (
                    <p className="text-xs text-gray-500 italic">
                      +{post.comments.length - 3} more comments
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PostFeed;
